import adminAxios from './adminAxios';
import type {
  Template,
  TemplateField,
  HierarchyNode,
  Role,
  AdminUser,
  UserAssignment,
  PermissionCatalogue,
  TemplateRelationship,
  CurrentAdmin,
} from '../../types/admin/admin.types';

type Envelope<T> = { success: boolean; message: string; data: T; pagination?: any };

const unwrap = <T,>(p: Promise<{ data: Envelope<T> }>) => p.then((r) => r.data);

export const adminService = {
  // ── Auth ───────────────────────────────────────────────────────────────────
  login: (username: string, password: string) =>
    adminAxios.post('/auth/login', { username, password }).then(
      (r) => r.data as { success: boolean; message: string; token: string; data: { id: number; username: string; name: string; is_super_admin: boolean } }
    ),
  me: () => unwrap<CurrentAdmin>(adminAxios.get('/auth/me')),
  changePassword: (current_password: string, new_password: string) =>
    unwrap<{}>(adminAxios.post('/auth/change-password', { current_password, new_password })),

  // ── Templates ──────────────────────────────────────────────────────────────
  listTemplates: (params?: Record<string, unknown>) =>
    unwrap<Template[]>(adminAxios.get('/templates', { params })),
  getTemplate: (id: number) => unwrap<Template>(adminAxios.get(`/templates/${id}`)),
  createTemplate: (body: Partial<Template>) => unwrap<Template>(adminAxios.post('/templates', body)),
  updateTemplate: (id: number, body: Partial<Template>) =>
    unwrap<Template>(adminAxios.put(`/templates/${id}`, body)),
  setTemplateStatus: (id: number, is_active: boolean, force = false) =>
    unwrap<Template>(adminAxios.patch(`/templates/${id}/status${force ? '?force=true' : ''}`, { is_active })),

  // ── Fields ─────────────────────────────────────────────────────────────────
  listFields: (templateId: number) =>
    unwrap<TemplateField[]>(adminAxios.get(`/templates/${templateId}/fields`)),
  createField: (templateId: number, body: Partial<TemplateField>) =>
    unwrap<TemplateField>(adminAxios.post(`/templates/${templateId}/fields`, body)),
  updateField: (templateId: number, fieldId: number, body: Partial<TemplateField>) =>
    unwrap<TemplateField>(adminAxios.put(`/templates/${templateId}/fields/${fieldId}`, body)),
  reorderFields: (templateId: number, order: number[]) =>
    unwrap<TemplateField[]>(adminAxios.patch(`/templates/${templateId}/fields/reorder`, { order })),
  setFieldStatus: (templateId: number, fieldId: number, is_active: boolean) =>
    unwrap<TemplateField>(adminAxios.patch(`/templates/${templateId}/fields/${fieldId}/status`, { is_active })),
  deleteField: (templateId: number, fieldId: number) =>
    unwrap<{}>(adminAxios.delete(`/templates/${templateId}/fields/${fieldId}`)),

  // ── Nodes ──────────────────────────────────────────────────────────────────
  listNodes: (params?: Record<string, unknown>) =>
    adminAxios.get('/nodes', { params }).then((r) => r.data as Envelope<HierarchyNode[]>),
  getNode: (id: number) => unwrap<HierarchyNode>(adminAxios.get(`/nodes/${id}`)),
  createNode: (body: { template_id: number; parent_node_id?: number | null; data: Record<string, unknown> }) =>
    unwrap<HierarchyNode>(adminAxios.post('/nodes', body)),
  updateNode: (id: number, body: { data?: Record<string, unknown>; parent_node_id?: number | null }) =>
    unwrap<HierarchyNode>(adminAxios.put(`/nodes/${id}`, body)),
  setNodeStatus: (id: number, is_active: boolean, force = false) =>
    unwrap<{ id: number; is_active: boolean }>(
      adminAxios.patch(`/nodes/${id}/status${force ? '?force=true' : ''}`, { is_active })
    ),
  nodeTree: (id: number, params?: Record<string, unknown>) =>
    adminAxios.get(`/nodes/${id}/tree`, { params }).then((r) => r.data as Envelope<HierarchyNode[]>),
  nodeBreadcrumb: (id: number) => unwrap<HierarchyNode[]>(adminAxios.get(`/nodes/${id}/breadcrumb`)),
  nodeLinks: (id: number, direction: 'to' | 'from' | 'both' = 'both') =>
    unwrap<any[]>(adminAxios.get(`/nodes/${id}/links`, { params: { direction } })),

  // ── Permissions / Roles ──────────────────────────────────────────────────────
  permissionCatalogue: () => unwrap<PermissionCatalogue>(adminAxios.get('/permissions')),
  listRoles: () => unwrap<Role[]>(adminAxios.get('/roles')),
  getRole: (id: number) => unwrap<Role>(adminAxios.get(`/roles/${id}`)),
  createRole: (body: { name: string; description?: string; permission_ids?: number[] }) =>
    unwrap<Role>(adminAxios.post('/roles', body)),
  updateRole: (id: number, body: { name?: string; description?: string }) =>
    unwrap<Role>(adminAxios.put(`/roles/${id}`, body)),
  setRolePermissions: (id: number, permission_ids: number[]) =>
    unwrap<Role>(adminAxios.put(`/roles/${id}/permissions`, { permission_ids })),
  deleteRole: (id: number, force = false) =>
    unwrap<{}>(adminAxios.delete(`/roles/${id}${force ? '?force=true' : ''}`)),

  // ── Users ──────────────────────────────────────────────────────────────────
  // Fetch the reserved user_profile template (with its fields), or null.
  getUserProfileTemplate: async (): Promise<Template | null> => {
    const list = await adminAxios.get('/templates').then((r) => r.data as Envelope<Template[]>);
    const tpl = (list.data || []).find((t) => t.template_key === 'user_profile');
    if (!tpl) return null;
    return adminAxios.get(`/templates/${tpl.id}`).then((r) => (r.data as Envelope<Template>).data);
  },
  listUsers: () => unwrap<AdminUser[]>(adminAxios.get('/users')),
  getUser: (id: number) => unwrap<AdminUser>(adminAxios.get(`/users/${id}`)),
  createUser: (body: Record<string, unknown>) => unwrap<AdminUser>(adminAxios.post('/users', body)),
  updateUser: (id: number, body: Record<string, unknown>) =>
    unwrap<AdminUser>(adminAxios.put(`/users/${id}`, body)),
  addAssignment: (userId: number, body: { role_id: number; node_id?: number | null }) =>
    unwrap<UserAssignment>(adminAxios.post(`/users/${userId}/assignments`, body)),
  removeAssignment: (userId: number, assignmentId: number) =>
    unwrap<{}>(adminAxios.delete(`/users/${userId}/assignments/${assignmentId}`)),

  // ── Relationships ────────────────────────────────────────────────────────────
  listRelationships: (params?: Record<string, unknown>) =>
    unwrap<TemplateRelationship[]>(adminAxios.get('/relationships', { params })),
};

export default adminService;
